import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import {
  CountdownChip,
  StepIndicatorColumn,
  StepCircle,
  StepLine,
  StepContent,
  StepItem,
} from 'loyalty/components/StatusMatchShared'
import useMyStatusMatchEnrollment, { type StatusMatchEnrollmentState } from 'loyalty/hooks/useMyStatusMatchEnrollment'
import { formatTimeRemaining } from 'loyalty/statusMatch/formatTimeRemaining'
import { resolveTemplate } from 'loyalty/statusMatch/resolveTemplate'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const GradientCard = styled.div`
  padding: ${rem(20)} ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.L};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const PageCard = styled(Pane)`
  padding: ${rem(24)};
`

const CardContent = styled.div`
  position: relative;
  z-index: 1;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
  margin-bottom: ${rem(4)};
`

const ButtonRight = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface CardItemProps {
  enrollmentState: StatusMatchEnrollmentState
  variant: 'page' | 'drawer'
  regionCode: string
  onLinkClick?: () => void
}

function StatusMatchChallengeCardItem({ enrollmentState, variant, regionCode, onLinkClick }: CardItemProps) {
  const intl = useIntl()
  const { enrollment, config, steps } = enrollmentState

  const templateVars = useMemo(() => {
    const metadata = enrollment.metadata ?? {}
    const vars: Record<string, string> = {}
    for (const [key, value] of Object.entries(metadata)) {
      if (typeof value === 'string') vars[key] = value
    }
    if (enrollment.expiresAt) {
      vars.expiresDate = intl.formatDate(enrollment.expiresAt, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    }
    return vars
  }, [enrollment, intl])

  const challengeCard = config.challengeCard
  const timeRemaining = formatTimeRemaining(enrollment.expiresAt, intl)
  const campaignPageUrl = `/${regionCode}/societe/${config.slug}`
  const browseOffersUrl = '/'

  const cardTitle = challengeCard?.cardTitle
    ? resolveTemplate(challengeCard.cardTitle, templateVars)
    : enrollment.campaignName

  const fallbackSubtitle = intl.formatMessage({
    id: 'KXcndHWDsevvKRAVd3ry',
    defaultMessage: 'Complete the steps below to unlock your matched status',
    description: 'Subtitle on the status match challenge card explaining what the user needs to do',
  })

  const cardSubtitle = challengeCard?.cardSubtitle
    ? resolveTemplate(challengeCard.cardSubtitle, templateVars)
    : fallbackSubtitle

  const browseOffersLabel = intl.formatMessage({
    id: 'axOyEP1a-x_5Yk60Wqwo',
    defaultMessage: 'Browse eligible offers',
    description: 'Button text on the status match challenge card linking to eligible offers search page',
  })

  const viewLabel = intl.formatMessage({
    id: 'KEAJRIhCiykUhqBqE_Re',
    defaultMessage: 'View',
    description: 'Link text on the status match challenge card to view the campaign page',
  })

  const stepsContent = steps.length > 0 && <div>
    {steps.map((step, index) => (
      <StepItem key={step.key}>
        <StepIndicatorColumn>
          <StepCircle eligible={step.eligible}>
            {step.eligible && <LineCheckIcon size="XXS" colour="dark-contrast" />}
          </StepCircle>
          {index < steps.length - 1 && <StepLine />}
        </StepIndicatorColumn>
        <StepContent>
          <BodyText variant="large" weight="bold">{step.title}</BodyText>
          <BodyText variant="large" colour="light-foreground-alt">{step.description}</BodyText>
        </StepContent>
      </StepItem>
    ))}
  </div>

  if (variant === 'drawer') {
    const tier = templateVars.targetTier?.toLowerCase() ?? 'silver'
    return <GradientCard className={`tier-${tier}`}>
      <CardContent>
        <VerticalSpacer gap={8}>
          <Header>
            <VerticalSpacer gap={4}>
              {timeRemaining && <CountdownChip>{timeRemaining}</CountdownChip>}
              <Heading variant="heading5">{cardTitle}</Heading>
            </VerticalSpacer>
            <TextButton kind="tertiary" size="small" to={campaignPageUrl} onClick={onLinkClick}>
              {viewLabel}
            </TextButton>
          </Header>
          {enrollment.campaignDescription && <Caption variant="medium" colour="light-foreground-alt">{enrollment.campaignDescription}</Caption>}
        </VerticalSpacer>
      </CardContent>
    </GradientCard>
  }

  return <PageCard>
    <VerticalSpacer gap={16}>
      <Header>
        <VerticalSpacer gap={4}>
          <Heading variant="heading3">{cardTitle}</Heading>
          <BodyText variant="large" colour="light-foreground-alt">{cardSubtitle}</BodyText>
        </VerticalSpacer>
        {timeRemaining && <CountdownChip>{timeRemaining}</CountdownChip>}
      </Header>
      {stepsContent}
      <Divider kind="primary" />
      <ButtonRight>
        <TextButton kind="primary" size="large" to={browseOffersUrl}>
          {browseOffersLabel}
        </TextButton>
      </ButtonRight>
    </VerticalSpacer>
  </PageCard>
}

/**
 * Renders Status Match challenge cards for each active enrollment in the user's region.
 */
interface Props {
  variant?: 'page' | 'drawer'
  onLinkClick?: () => void
}

function StatusMatchChallengeCard({ variant = 'page', onLinkClick }: Props) {
  const { enrollmentStates } = useMyStatusMatchEnrollment()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  if (enrollmentStates.length === 0) return null

  const cards = <VerticalSpacer gap={16}>
    {enrollmentStates.map(state => (
      <StatusMatchChallengeCardItem
        key={state.config.campaignId}
        enrollmentState={state}
        variant={variant}
        regionCode={regionCode}
        onLinkClick={onLinkClick}
      />
    ))}
  </VerticalSpacer>

  if (variant === 'page') {
    return <Sheet padding="24 0" tabletPadding="48 0" background="light-background">
      <LayoutContainer variant="medium">{cards}</LayoutContainer>
    </Sheet>
  }

  return cards
}

export default StatusMatchChallengeCard
